import { hourOf, sun } from "../sim/time";
import { Tile, type WorldState } from "../sim/types";
import { tilesNow } from "../sim/player";
import type { Atlas } from "./assets";
import { shakeCam, VIEW_SCALE, type Cam } from "./camera";
import { C, TILE_FALLBACK } from "./palette";

const TS = 32;

const TILE_KEY: Record<number, string> = {
  [Tile.Grass]: "grass",
  [Tile.Dirt]: "dirt",
  [Tile.Path]: "path",
  [Tile.Soil]: "soil",
  [Tile.Crop]: "grass",
  [Tile.Water]: "water",
  [Tile.Tall]: "grass",
  [Tile.Wood]: "wood",
  [Tile.Floor]: "wood",
  [Tile.Hollow]: "hollow",
  [Tile.Vein]: "hollow",
  [Tile.Exit]: "hollow",
  [Tile.Well]: "dirt",
};

const keyed = new WeakMap<HTMLImageElement, HTMLCanvasElement>();

function keyMagenta(img: HTMLImageElement): HTMLCanvasElement {
  const hit = keyed.get(img);
  if (hit) return hit;
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const g = c.getContext("2d")!;
  g.drawImage(img, 0, 0);
  const data = g.getImageData(0, 0, c.width, c.height);
  const d = data.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i]!;
    const green = d[i + 1]!;
    const b = d[i + 2]!;
    // True magenta plus the dusty-rose key used on generated art.
    if (green < 100 && r > 150 && b > 110 && r - green > 55 && b - green > 25) d[i + 3] = 0;
  }
  g.putImageData(data, 0, 0);
  keyed.set(img, c);
  return c;
}

function drawGroundTile(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  tile: number,
  x: number,
  y: number,
  dx: number,
  dy: number,
  tw: number,
): void {
  const varied =
    tile === Tile.Grass || tile === Tile.Tall || tile === Tile.Crop || tile === Tile.Dirt || tile === Tile.Path;
  if (varied && img.width >= 16 && img.height >= 16) {
    const half = Math.floor(Math.min(img.width, img.height) / 2);
    const sx = ((x ^ (y << 1)) & 1) * half;
    const sy = ((x + y) & 1) * half;
    ctx.drawImage(img, sx, sy, half, half, dx, dy, tw + 0.5, tw + 0.5);
    const shade = ((x * 7 + y * 11) & 7) / 220;
    if (shade > 0) {
      ctx.fillStyle = `rgba(0,0,0,${shade})`;
      ctx.fillRect(dx, dy, tw + 0.5, tw + 0.5);
    }
  } else {
    ctx.drawImage(img, dx, dy, tw + 0.5, tw + 0.5);
  }
}

function drawTree(ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number): void {
  const seed = (x * 17 + y * 31) | 0;
  const lean = ((seed % 5) - 2) * 0.6;
  const tall = 11 + (seed % 4);
  const wide = 10 + (seed % 3);
  ctx.fillStyle = "#2a1c10";
  ctx.fillRect(dx + 14 + lean, dy + 20, 5, 12);
  ctx.fillStyle = "#1a2410";
  ctx.beginPath();
  ctx.ellipse(dx + 16 + lean, dy + 14, wide, tall, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#2a3818";
  ctx.beginPath();
  ctx.ellipse(dx + 14 + lean, dy + 12, wide * 0.72, tall * 0.78, 0, 0, Math.PI * 2);
  ctx.fill();
}

export function drawWorld(
  ctx: CanvasRenderingContext2D,
  w: WorldState,
  atlas: Atlas,
  cam: Cam,
  viewW: number,
  viewH: number,
): void {
  const sh = shakeCam(cam, w);
  const scale = VIEW_SCALE;
  ctx.fillStyle = w.mode === "hollow" ? C.coal : "#121410";
  ctx.fillRect(0, 0, viewW, viewH);
  ctx.save();
  ctx.translate(viewW / 2, viewH / 2);
  ctx.scale(scale, scale);
  ctx.translate(-sh.x * TS, -sh.y * TS);

  const map = tilesNow(w);
  const tw = TS;
  const halfW = viewW / (2 * scale * TS);
  const halfH = viewH / (2 * scale * TS);
  const visX0 = Math.max(0, Math.floor(sh.x - halfW) - 1);
  const visY0 = Math.max(0, Math.floor(sh.y - halfH) - 1);
  const visX1 = Math.min(map.w, Math.ceil(sh.x + halfW) + 2);
  const visY1 = Math.min(map.h, Math.ceil(sh.y + halfH) + 2);

  ctx.fillStyle = "#1a2016";
  ctx.fillRect(-tw * 2, -tw * 2, (map.w + 4) * tw, (map.h + 4) * tw);

  for (let y = visY0; y < visY1; y++) {
    for (let x = visX0; x < visX1; x++) {
      const t = map.tiles[y * map.w + x]!;
      const key = TILE_KEY[t];
      const img = key ? atlas.tiles[key] : undefined;
      const dx = x * tw;
      const dy = y * tw;
      if (img) drawGroundTile(ctx, img, t, x, y, dx, dy, tw);
      else {
        ctx.fillStyle = TILE_FALLBACK[t] ?? C.bark;
        ctx.fillRect(dx, dy, tw + 0.5, tw + 0.5);
      }
      if (t === Tile.Tree) drawTree(ctx, x, y, dx, dy);
      if (t === Tile.Wall || t === Tile.Roof || t === Tile.Ruin) {
        ctx.fillStyle = t === Tile.Ruin ? "#3a322c" : "#2a221c";
        ctx.fillRect(dx, dy, tw + 0.5, tw + 0.5);
        ctx.fillStyle = "#1a1410";
        ctx.fillRect(dx, dy, tw + 0.5, 3);
        // Ground-contact shadow so buildings read less flat
        ctx.fillStyle = "rgba(0,0,0,0.22)";
        ctx.fillRect(dx + 2, dy + tw - 4, tw - 2, 5);
      }
      if (t === Tile.Fence) {
        ctx.fillStyle = "#4a3820";
        ctx.fillRect(dx + 6, dy, 4, tw);
        ctx.fillRect(dx, dy + 10, tw, 3);
      }
      if (t === Tile.Crop) {
        ctx.fillStyle = "#5a7a38";
        ctx.fillRect(dx + 8, dy + 10, 6, 14);
        ctx.fillRect(dx + 16, dy + 8, 6, 16);
      }
      if (t === Tile.Well || t === Tile.Exit) {
        const well = atlas.sprites.well;
        if (well) ctx.drawImage(keyMagenta(well), dx - 8, dy - 16, tw + 16, tw + 20);
        else {
          ctx.fillStyle = "#6a2a22";
          ctx.beginPath();
          ctx.arc(dx + 16, dy + 16, 12, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#050304";
          ctx.beginPath();
          ctx.arc(dx + 16, dy + 16, 7, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  for (const d of w.memory) {
    if (d.layer === "hollow" && w.mode !== "hollow") continue;
    if (d.layer === "overworld" && w.mode !== "overworld") continue;
    if (d.layer === "home" && w.mode !== "home") continue;
    const a = d.life;
    ctx.fillStyle = d.kind === "blood" ? `rgba(120,28,22,${0.55 * a})` : `rgba(40,40,36,${0.4 * a})`;
    ctx.beginPath();
    ctx.ellipse(d.x * TS, d.y * TS, d.kind === "blood" ? 6 : 4, d.kind === "blood" ? 4 : 3, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  if (w.mode === "overworld") {
    for (const n of w.npcs) {
      if (n.memory.missing || !n.alive) continue;
      drawActor(ctx, atlas, npcSprite(n.id, atlas), n.x, n.y, 0.92);
    }
  }

  if (w.mode === "hollow" && w.hollow) {
    for (const l of w.hollow.loot) {
      if (l.taken) continue;
      const lx = l.x * TS;
      const ly = l.y * TS;
      const pulse = 0.55 + Math.sin(w.timeSec * 4 + l.x) * 0.15;
      ctx.fillStyle = `rgba(232,213,163,${0.12 * pulse})`;
      ctx.beginPath();
      ctx.arc(lx, ly, 14, 0, Math.PI * 2);
      ctx.fill();
      const icon = atlas.icons[l.item.id];
      if (icon) ctx.drawImage(icon, lx - 10, ly - 10, 20, 20);
      else {
        ctx.fillStyle = C.lantern;
        ctx.fillRect(lx - 3, ly - 3, 6, 6);
      }
    }
    for (const e of w.hollow.enemies) {
      if (e.state === "dead") continue;
      const spr = atlas.sprites[e.kind] ?? atlas.sprites.lurcher;
      drawActor(ctx, atlas, spr, e.x, e.y, 1.05, e.state === "hunt");
    }
  }

  if (w.mode === "home") {
    ctx.fillStyle = `rgba(80,40,20,${w.home.dirt * 0.25})`;
    ctx.fillRect(TS, TS, 10 * TS, 8 * TS);
    ctx.fillStyle = `rgba(120,28,22,${w.home.blood * 0.35})`;
    ctx.fillRect(2 * TS, 6 * TS, 40, 12);
    for (let i = 0; i < w.home.bench.length; i++) {
      const it = w.home.bench[i];
      ctx.fillStyle = C.rust;
      ctx.fillRect((7 + i) * TS, 3 * TS, 22, 14);
      if (it) {
        const ic = atlas.icons[it.id];
        if (ic) ctx.drawImage(ic, (7 + i) * TS, 2.2 * TS, 22, 22);
      }
    }
  }

  const striking = w.player.strikePhase === "active" || w.player.strikePhase === "wind";
  drawActor(ctx, atlas, atlas.sprites.player, w.player.x, w.player.y, 1, w.player.strikePhase === "active");
  if (w.player.lantern) {
    const g = ctx.createRadialGradient(w.player.x * TS, w.player.y * TS, 4, w.player.x * TS, w.player.y * TS, 90);
    g.addColorStop(0, "rgba(232,213,163,0.16)");
    g.addColorStop(1, "rgba(232,213,163,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(w.player.x * TS, w.player.y * TS, 90, 0, Math.PI * 2);
    ctx.fill();
  }
  if (striking) {
    const f = w.player.facing.x || w.player.facing.y ? w.player.facing : { x: 0, y: 1 };
    const ang = Math.atan2(f.y, f.x);
    const active = w.player.strikePhase === "active";
    ctx.strokeStyle = active ? "rgba(232,213,163,0.88)" : "rgba(232,213,163,0.32)";
    ctx.lineWidth = active ? 3 : 2;
    ctx.beginPath();
    ctx.arc(w.player.x * TS, w.player.y * TS, active ? 24 : 18, ang - 1.05, ang + (active ? 0.35 : 0.95));
    ctx.stroke();
  }

  drawNight(ctx, w, sh, viewW, viewH, scale);
  ctx.restore();
}

function npcSprite(id: string, atlas: Atlas): HTMLImageElement | undefined {
  return atlas.sprites[id] ?? atlas.sprites.player;
}

function drawActor(
  ctx: CanvasRenderingContext2D,
  _atlas: Atlas,
  img: HTMLImageElement | undefined,
  x: number,
  y: number,
  s = 1,
  flash = false,
): void {
  const px = x * TS;
  const py = y * TS;
  if (img) {
    const k = keyMagenta(img);
    const w = 28 * s;
    const h = 34 * s;
    ctx.drawImage(k, px - w / 2, py - h + 6, w, h);
  } else {
    ctx.fillStyle = flash ? C.lantern : C.bone;
    ctx.beginPath();
    ctx.ellipse(px, py, 6 * s, 4 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = C.bark;
    ctx.fillRect(px - 4 * s, py - 16 * s, 8 * s, 14 * s);
  }
}

function drawNight(
  ctx: CanvasRenderingContext2D,
  w: WorldState,
  sh: { x: number; y: number },
  viewW: number,
  viewH: number,
  scale: number,
): void {
  const { night } = sun(w.timeSec);
  let cover = w.mode === "hollow" ? 0.78 : night * 0.72;
  if (w.weather === "storm") cover = Math.max(cover, 0.45);
  if (w.weather === "fog") cover = Math.max(cover, 0.28);
  if (cover <= 0.04) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = `rgba(8,10,14,${cover})`;
  ctx.fillRect(0, 0, viewW, viewH);
  if (w.player.lantern || hourOf(w.timeSec) > 5 && hourOf(w.timeSec) < 20 && w.mode !== "hollow") {
    const gx = viewW / 2 + (w.player.x - sh.x) * TS * scale;
    const gy = viewH / 2 + (w.player.y - sh.y) * TS * scale;
    const rad = (w.mode === "hollow" || w.player.lantern ? 160 : 220) * (w.weather === "fog" ? 0.7 : 1);
    const g = ctx.createRadialGradient(gx, gy, 20, gx, gy, rad);
    g.addColorStop(0, "rgba(8,10,14,0)");
    g.addColorStop(1, `rgba(8,10,14,${cover})`);
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, viewW, viewH);
    ctx.globalCompositeOperation = "source-over";
  }
  ctx.restore();
}
